import type { IssuedLink } from '@obras/shared';
import { formatDateTime, ROLE_LABELS } from '@obras/shared';
import { Ban, CircleCheck, KeyRound, Search, Users } from 'lucide-react';
import { useState } from 'react';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { Badge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { SearchInput } from '../components/ui/Field.js';
import { Avatar, EmptyState, ErrorState, Skeleton } from '../components/ui/Feedback.js';
import { LinkBox } from '../components/ui/LinkBox.js';
import { Modal } from '../components/ui/Modal.js';
import { SingleSelect } from '../components/ui/MultiSelect.js';
import {
  useOrganizations,
  usePlatformPasswordLink,
  usePlatformUsers,
  useSetPlatformUserStatus,
} from '../features/plataforma/api.js';
import { useAuth } from '../lib/auth.js';

const ICON = { strokeWidth: 1.5 } as const;

export function PlataformaUsuariosPage() {
  const { user: current } = useAuth();
  const [search, setSearch] = useState('');
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [passwordLink, setPasswordLink] = useState<IssuedLink | null>(null);

  const organizations = useOrganizations();

  const { data, isPending, isError, error, refetch } = usePlatformUsers({
    search: search || undefined,
    ...(organizationId ? { organizationId } : {}),
    perPage: 100,
  });

  const issueLink = usePlatformPasswordLink();
  const setStatus = useSetPlatformUserStatus();

  return (
    <AppShell>
      <PageHeader
        title="Usuários"
        description="Todas as pessoas do sistema, e em quais organizações elas atuam."
      />

      <div className="mb-4 flex flex-wrap items-end justify-end gap-2">
        {/*
          Filtro por órgão, com busca: a lista cresce com os clientes, e caçar
          visualmente entre dezenas de prefeituras é pior que digitar o nome.
          "Todas" é uma opção explícita — deixar a limpeza só no X do campo
          esconde o caminho de volta.
        */}
        <div className="w-full sm:w-72">
          <SingleSelect
            label="Organização"
            placeholder="Todas as organizações"
            options={[
              { value: '', label: 'Todas as organizações' },
              ...(organizations.data?.items ?? []).map((organization) => ({
                value: organization.id,
                label: organization.name,
                detail: `${organization.usersCount} usuário${organization.usersCount === 1 ? '' : 's'}`,
              })),
            ]}
            value={organizationId ?? ''}
            onChange={(valor) => setOrganizationId(valor === '' ? null : valor)}
            searchable={(organizations.data?.items ?? []).length > 6}
          />
        </div>

        <div className="relative w-full sm:w-80">
          <Search
            {...ICON}
            size={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome ou e-mail"
            aria-label="Buscar usuários"
            className="pr-3 pl-9"
          />
        </div>
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : data.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={Users}
            title="Nenhum usuário"
            description="As pessoas aparecem aqui conforme as organizações as cadastram."
          />
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left">
            <caption className="sr-only">Usuários da plataforma</caption>
            <thead className="bg-muted">
              <tr className="text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
                <th scope="col" className="px-5 py-3">
                  Pessoa
                </th>
                <th scope="col" className="px-5 py-3">
                  Organizações
                </th>
                <th scope="col" className="px-5 py-3">
                  Situação
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Último acesso
                </th>
                <th scope="col" className="px-5 py-3" />
              </tr>
            </thead>

            <tbody>
              {data.items.map((person) => {
                const isSelf = person.id === current?.id;

                return (
                  <tr key={person.id} className="border-t border-border">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={person.name} size={32} />
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 truncate text-[14px] font-medium">
                            {person.name}
                            {person.isPlatformAdmin ? (
                              <Badge tone="primary">Plataforma</Badge>
                            ) : null}
                          </p>
                          <p className="truncate font-mono text-[11px] text-muted-foreground">
                            {person.email}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-3">
                      {person.memberships.length === 0 ? (
                        <span className="text-[13px] text-muted-foreground">nenhuma</span>
                      ) : (
                        <ul className="flex flex-col gap-0.5">
                          {person.memberships.map((membership) => (
                            <li key={membership.organizationId} className="text-[13px]">
                              {membership.organizationName}
                              <span className="ml-1.5 text-[12px] text-muted-foreground">
                                {ROLE_LABELS[membership.role]}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>

                    <td className="px-5 py-3">
                      <Badge
                        tone={
                          person.status === 'active'
                            ? 'success'
                            : person.status === 'invited'
                              ? 'warning'
                              : 'danger'
                        }
                      >
                        {person.status === 'active'
                          ? 'Ativo'
                          : person.status === 'invited'
                            ? 'Aguardando senha'
                            : 'Desativado'}
                      </Badge>
                    </td>

                    <td className="tabular px-5 py-3 text-right font-mono text-[11px] text-muted-foreground">
                      {person.lastLoginAt ? formatDateTime(person.lastLoginAt) : 'nunca'}
                    </td>

                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<KeyRound {...ICON} size={14} />}
                          onClick={() =>
                            issueLink.mutate(person.id, { onSuccess: setPasswordLink })
                          }
                        >
                          Senha
                        </Button>

                        {isSelf ? null : (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={
                              person.status === 'suspended' ? (
                                <CircleCheck {...ICON} size={14} />
                              ) : (
                                <Ban {...ICON} size={14} />
                              )
                            }
                            onClick={() =>
                              setStatus.mutate({
                                userId: person.id,
                                status: person.status === 'suspended' ? 'active' : 'suspended',
                              })
                            }
                          >
                            {person.status === 'suspended' ? 'Reativar' : 'Desativar'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={passwordLink !== null}
        onClose={() => setPasswordLink(null)}
        title="Link de senha"
        description="Entregue à pessoa para ela definir a própria senha."
      >
        {passwordLink ? (
          <LinkBox link={passwordLink} onClose={() => setPasswordLink(null)} />
        ) : null}
      </Modal>
    </AppShell>
  );
}
